import styled from 'styled-components';
import { rem } from 'polished';
import { glassmorphism } from 'styles/core/gradients';
import { fadeInAnimation } from 'styles/core/animations';

export const Section = styled.section`
  padding: ${rem('80px')} 0;
  background: #f8fafc;
  color: #1f2937;
`;

export const DashboardContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${rem('60px')};
  align-items: center;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;

  @media ${({ theme }) => theme.mediaQueries.mediumMax} {
    grid-template-columns: 1fr;
    gap: ${rem('40px')};
    text-align: center;
  }
`;

export const ReversedDashboardContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${rem('60px')};
  align-items: center;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;

  @media ${({ theme }) => theme.mediaQueries.mediumMax} {
    grid-template-columns: 1fr;
    gap: ${rem('40px')};
    text-align: center;

    & > *:first-child {
      order: 2;
    }
    & > *:last-child {
      order: 1;
    }
  }
`;

export const LeftContent = styled.div``;

export const SectionTitle = styled.h2`
  font-family: 'MontserratBlack', sans-serif;
  color: #1f2937;
  margin-bottom: ${rem('20px')};
  font-size: clamp(1.8rem, 4vw, 2.8rem);
  line-height: 1.3;
  letter-spacing: -0.5px;
  font-weight: 800;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

export const SectionDescription = styled.p`
  font-size: ${rem('18px')};
  color: #4b5563;
  line-height: 1.6;
  margin-bottom: ${rem('30px')};
  max-width: 500px;
`;

export const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${rem('20px')};

  @media ${({ theme }) => theme.mediaQueries.smallMax} {
    grid-template-columns: 1fr;
    gap: ${rem('16px')};
  }
`;

export const FeatureCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem('16px')};
  padding: ${rem('24px')};
  background: white;
  border-radius: 12px;
  border-left: 3px solid ${({ $accent }) => $accent || '#fbbf24'};
  transition: all 0.2s ease;

  &:hover {
    background: #fafafa;
  }
`;

export const FeatureIcon = styled.div`
  flex-shrink: 0;
  margin-top: ${rem('2px')};
`;

export const FeatureContent = styled.div`
  flex: 1;
`;

export const FeatureTitle = styled.h3`
  color: #1f2937;
  font-size: ${rem('17px')};
  font-weight: 700;
  margin-bottom: ${rem('6px')};
  line-height: 1.3;
`;

export const FeatureDescription = styled.p`
  color: #6b7280;
  font-size: ${rem('15px')};
  line-height: 1.5;
  margin: 0;
`;

export const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(300px, 100%), 1fr));
  gap: ${rem('30px')};
  margin-top: ${rem('25px')};
  width: 100%;

  @media ${({ theme }) => theme.mediaQueries.small} {
    grid-template-columns: 1fr;
    gap: ${rem('20px')};
  }
`;

export const ServiceCard = styled.div`
  ${glassmorphism};
  border-radius: 24px;
  padding: 0;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;
  overflow: hidden;
  ${fadeInAnimation};
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.1),
    0 2px 8px rgba(124, 58, 237, 0.1);
  min-height: 500px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(
      90deg,
      ${({ theme }) => theme.colors.gradientStart} 0%,
      ${({ theme }) => theme.colors.gradientEnd} 100%
    );
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s ease;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(124, 58, 237, 0.02) 0%,
      rgba(30, 64, 175, 0.02) 100%
    );
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow:
      0 20px 40px rgba(0, 0, 0, 0.15),
      0 4px 16px rgba(124, 58, 237, 0.2);

    &::before {
      transform: scaleX(1);
    }

    &::after {
      opacity: 1;
    }
  }
`;

export const CardHeader = styled.div`
  background: linear-gradient(
    135deg,
    rgba(124, 58, 237, 0.05) 0%,
    rgba(30, 64, 175, 0.03) 100%
  );
  padding: ${rem('30px')} ${rem('25px')} ${rem('20px')};
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 24px 24px 0 0;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(124, 58, 237, 0.2) 50%,
      transparent 100%
    );
  }
`;

export const CardContent = styled.div`
  padding: ${rem('25px')};
  display: flex;
  flex-direction: column;
  flex: 1;
  text-align: center;
`;

export const ServiceTitle = styled.h3`
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.gradientStart} 0%,
    ${({ theme }) => theme.colors.gradientEnd} 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: ${rem('24px')};
  margin-bottom: ${rem('20px')};
  font-weight: 700;
`;

export const ServiceDescription = styled.p`
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.8;
  font-size: ${rem('16px')};
  font-weight: 500;
  margin: 0 0 ${rem('24px')} 0;
  opacity: 0.9;
`;

export const ServiceFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 ${rem('30px')} 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${rem('12px')};

  @media ${({ theme }) => theme.mediaQueries.small} {
    grid-template-columns: 1fr;
  }
`;

export const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  font-size: ${rem('14px')};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  opacity: 0.8;

  span {
    color: ${({ theme }) => theme.colors.gradientStart};
    font-weight: 700;
    margin-right: ${rem('8px')};
    font-size: ${rem('12px')};
  }
`;

export const DemoButton = styled.button`
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.ctaSecondary} 0%,
    #059669 100%
  );
  color: white;
  border: none;
  border-radius: 12px;
  padding: ${rem('16px')} ${rem('32px')};
  font-size: ${rem('14px')};
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  margin-top: auto;
  position: relative;
  overflow: hidden;
  text-transform: none;
  letter-spacing: 0.3px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s ease;
  }

  &:hover {
    transform: translateY(-2px) scale(1.02);
    box-shadow:
      0 8px 25px rgba(16, 185, 129, 0.3),
      0 2px 8px rgba(0, 0, 0, 0.1);
    background: linear-gradient(
      135deg,
      #059669 0%,
      ${({ theme }) => theme.colors.ctaSecondary} 100%
    );

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(0) scale(1.01);
  }
`;
